export interface PixelAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
  hue?: number;
  blurRadius?: number;
}

const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

/** Probes actual pixels on an isolated canvas: some WebViews expose `filter` but ignore it while drawing. */
export function supportsCanvasFilters(context: CanvasRenderingContext2D): boolean {
  if (!("filter" in context) || typeof context.filter !== "string" || typeof document === "undefined") return false;
  const canvas = document.createElement("canvas");
  canvas.width = 2;
  canvas.height = 1;
  let probe: CanvasRenderingContext2D | null = null;
  try {
    probe = canvas.getContext("2d", { willReadFrequently: true });
    if (!probe || !("filter" in probe) || typeof probe.filter !== "string") return false;
    probe.save();
    probe.filter = "none";
    probe.fillStyle = "rgb(224, 192, 128)";
    probe.fillRect(0, 0, 2, 1);
    const baseline = probe.getImageData(0, 0, 1, 1).data;
    probe.clearRect(0, 0, 2, 1);
    probe.filter = "brightness(0.5)";
    if (probe.filter === "none") return false;
    probe.fillStyle = "rgb(224, 192, 128)";
    probe.fillRect(0, 0, 2, 1);
    const filtered = probe.getImageData(0, 0, 1, 1).data;
    const totalDarkening = baseline[0] - filtered[0] + baseline[1] - filtered[1] + baseline[2] - filtered[2];
    return baseline[3] >= 240 && filtered[3] >= 240
      && totalDarkening >= 150
      && filtered[0] <= baseline[0] - 30
      && filtered[1] <= baseline[1] - 30
      && filtered[2] <= baseline[2] - 30;
  } catch {
    return false;
  } finally {
    try { probe?.restore(); } catch { /* The disposable probe is still released below. */ }
    canvas.width = 0;
    canvas.height = 0;
  }
}

function applyColorAdjustments(data: Uint8ClampedArray, adjustments: PixelAdjustments): void {
  const brightness = adjustments.brightness / 100;
  const contrast = adjustments.contrast / 100;
  const saturation = adjustments.saturation / 100;
  const hue = (adjustments.hue || 0) * Math.PI / 180;
  const hueCos = Math.cos(hue);
  const hueSin = Math.sin(hue);

  for (let index = 0; index < data.length; index += 4) {
    let red = (data[index] * brightness - 128) * contrast + 128;
    let green = (data[index + 1] * brightness - 128) * contrast + 128;
    let blue = (data[index + 2] * brightness - 128) * contrast + 128;

    const luminance = red * .213 + green * .715 + blue * .072;
    red = luminance + (red - luminance) * saturation;
    green = luminance + (green - luminance) * saturation;
    blue = luminance + (blue - luminance) * saturation;

    if (hue !== 0) {
      const sourceRed = red;
      const sourceGreen = green;
      const sourceBlue = blue;
      red = (.213 + hueCos * .787 - hueSin * .213) * sourceRed
        + (.715 - hueCos * .715 - hueSin * .715) * sourceGreen
        + (.072 - hueCos * .072 + hueSin * .928) * sourceBlue;
      green = (.213 - hueCos * .213 + hueSin * .143) * sourceRed
        + (.715 + hueCos * .285 + hueSin * .140) * sourceGreen
        + (.072 - hueCos * .072 - hueSin * .283) * sourceBlue;
      blue = (.213 - hueCos * .213 - hueSin * .787) * sourceRed
        + (.715 - hueCos * .715 + hueSin * .715) * sourceGreen
        + (.072 + hueCos * .928 + hueSin * .072) * sourceBlue;
    }

    data[index] = clampByte(red);
    data[index + 1] = clampByte(green);
    data[index + 2] = clampByte(blue);
  }
}

function blurPixels(data: Uint8ClampedArray, width: number, height: number, radius: number): void {
  const boundedRadius = Math.min(64, Math.max(1, Math.round(radius)));
  const horizontal = new Uint8ClampedArray(data.length);
  const diameter = boundedRadius * 2 + 1;

  for (let y = 0; y < height; y += 1) {
    for (let channel = 0; channel < 4; channel += 1) {
      let sum = data[(y * width) * 4 + channel] * boundedRadius;
      for (let x = 0; x <= boundedRadius; x += 1) sum += data[(y * width + Math.min(width - 1, x)) * 4 + channel];
      for (let x = 0; x < width; x += 1) {
        horizontal[(y * width + x) * 4 + channel] = sum / diameter;
        const removeX = Math.max(0, x - boundedRadius);
        const addX = Math.min(width - 1, x + boundedRadius + 1);
        sum += data[(y * width + addX) * 4 + channel] - data[(y * width + removeX) * 4 + channel];
      }
    }
  }

  for (let x = 0; x < width; x += 1) {
    for (let channel = 0; channel < 4; channel += 1) {
      let sum = horizontal[x * 4 + channel] * boundedRadius;
      for (let y = 0; y <= boundedRadius; y += 1) sum += horizontal[(Math.min(height - 1, y) * width + x) * 4 + channel];
      for (let y = 0; y < height; y += 1) {
        data[(y * width + x) * 4 + channel] = sum / diameter;
        const removeY = Math.max(0, y - boundedRadius);
        const addY = Math.min(height - 1, y + boundedRadius + 1);
        sum += horizontal[(addY * width + x) * 4 + channel] - horizontal[(removeY * width + x) * 4 + channel];
      }
    }
  }
}

/** Mutates ImageData exactly once; preview and export both consume the resulting cached canvas. */
export function applyPixelAdjustments(data: Uint8ClampedArray, width: number, height: number, adjustments: PixelAdjustments): void {
  applyColorAdjustments(data, adjustments);
  if ((adjustments.blurRadius || 0) >= .5) blurPixels(data, width, height, adjustments.blurRadius || 0);
}

export function hasColorAdjustments(adjustments: PixelAdjustments): boolean {
  return adjustments.brightness !== 100 || adjustments.contrast !== 100 || adjustments.saturation !== 100
    || (adjustments.hue || 0) !== 0 || (adjustments.blurRadius || 0) >= .5;
}
